import React from 'react';
import { Typography, TypographyProps, SxProps, Theme } from '@mui/material';
import { colors, typographyVariants } from '../../tokens';

export interface BodyTextProps extends Omit<TypographyProps, 'variant'> {
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Size variant of the body text
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Color variant
   * @default 'secondary'
   */
  colorVariant?: 'primary' | 'secondary' | 'tertiary' | 'muted';
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
}

/**
 * BodyText - Standard body text for paragraphs and content
 * 
 * Use cases:
 * - Paragraph text
 * - Content descriptions
 * - List items
 * - Modal/dialog content
 * - General readable text
 * 
 * @example
 * ```tsx
 * <BodyText>This is standard body text.</BodyText>
 * <BodyText size="large">Larger body text for emphasis.</BodyText>
 * <BodyText size="small" colorVariant="tertiary">Helper text</BodyText>
 * ```
 */
export const BodyText: React.FC<BodyTextProps> = ({ 
  children, 
  size = 'medium',
  colorVariant = 'secondary',
  sx = {}, 
  ...props 
}) => {
  const sizeMap = {
    small: {
      fontSize: typographyVariants.bodySmall.fontSize,
      lineHeight: typographyVariants.bodySmall.lineHeight,
    },
    medium: {
      fontSize: typographyVariants.body.fontSize,
      lineHeight: typographyVariants.body.lineHeight,
    },
    large: {
      fontSize: typographyVariants.bodyLarge.fontSize,
      lineHeight: typographyVariants.bodyLarge.lineHeight,
    },
  };

  const colorMap = {
    primary: colors.text.primary,
    secondary: colors.text.secondary,
    tertiary: colors.text.tertiary,
    muted: colors.text.muted,
  };

  return (
    <Typography
      sx={{
        fontFamily: typographyVariants.body.fontFamily,
        fontWeight: typographyVariants.body.fontWeight,
        color: colorMap[colorVariant],
        ...sizeMap[size],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};
